import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/app/data.service';

/**
 *  BÉREK ÉS PÓTLÉKOK – érvényességgel.
 *
 *  A bér nem egyetlen felülírható szám: minden tétel egy időszak (érvényes ettől–eddig).
 *  Módosításkor nem íródik felül a régi állapot, hanem lezárul, és új időszak nyílik –
 *  így a korábbi riportok és a már kiadott iratok összegei visszamenőleg sem változnak.
 *
 *  Innen veszi a dokumentumgenerátor a pótlék-jogcímek gyűjtőjét és az adott napon
 *  érvényes bért is.
 */
@Component({
  selector: 'app-ber',
  templateUrl: './ber.component.html',
  styleUrls: ['./ber.component.scss'],
})
export class BerComponent implements OnInit {
  sajatIntezmeny: any;
  jogosult = false;
  betoltes = false;

  /** Melyik napra nézzük a béreket (a lista ezt az állapotot mutatja). */
  datum = '';
  kereses = '';
  dolgozok: any[] = [];

  // Pótlék-jogcímek közös gyűjtője
  jogcimek: any[] = [];
  gyujtoNyitva = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    const jogkor = await this.data.getJogkor();
    // A hozzáférés a Jogosultságok szerkesztőből jön, nem az 'admin' jogkör nevéből –
    // így más jogkör is megkaphatja ezt a képernyőt (/admin/ber).
    this.jogosult = (await this.data.hozzaferesek()).szabad('/admin/ber');
    this.datum = this.mai();
    this.jogcimekBetolt();
    this.betolt();
  }

  get intezmenyId(): string {
    return String(this.sajatIntezmeny?.intezmenyId || this.sajatIntezmeny?.id || '').trim();
  }

  private mai(): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }

  /** Az <ion-input type="date"> csak 'YYYY-MM-DD'-t tud megjeleníteni. */
  private nap(v: any): string {
    return String(v || '').slice(0, 10);
  }

  betolt() {
    if (!this.intezmenyId) return;
    this.betoltes = true;
    this.http.get<any>(`/api/allomany-ber/allomany/${this.intezmenyId}?datum=${this.datum}`).subscribe({
      next: (r) => {
        this.betoltes = false;
        this.dolgozok = (r?.dolgozok || []).map((d: any) => ({
          ...d,
          nyitva: false,
          tortenet: null,
          // A rögzítő űrlap kiinduló állapota a most érvényes bér.
          urlap: null,
        }));
      },
      error: (e) => {
        this.betoltes = false;
        this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a béreket.', 'error');
      },
    });
  }

  get szurtDolgozok(): any[] {
    const q = this.kulcs(this.kereses);
    if (!q) return this.dolgozok;
    return this.dolgozok.filter((d) => this.kulcs(`${d.nev} ${d.adid} ${d.munkakor}`).includes(q));
  }

  private kulcs(s: any): string {
    return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  ft(v: any): string {
    const n = Math.round(Number(v) || 0);
    return `${n.toLocaleString('hu-HU')} Ft`;
  }

  /* ── Jogcím-gyűjtő ── */

  jogcimekBetolt() {
    if (!this.intezmenyId) return;
    this.http.get<any[]>(`/api/allomany-ber/jogcimek/${this.intezmenyId}`).subscribe({
      next: (r) => { this.jogcimek = (Array.isArray(r) ? r : []).map((j) => ({ ...j })); },
      error: () => { /* gyűjtő nélkül is rögzíthető egyedi pótlék */ },
    });
  }

  ujJogcim() {
    this.jogcimek.push({ id: null, nev: '', alap_osszeg: 0 });
  }

  torolJogcim(i: number) {
    this.jogcimek.splice(i, 1);
  }

  /** A gyűjtő sorrendje adja a dokumentumok bértáblájának sorrendjét is. */
  mozgatJogcim(i: number, irany: number) {
    const cel = i + irany;
    if (cel < 0 || cel >= this.jogcimek.length) return;
    const [elem] = this.jogcimek.splice(i, 1);
    this.jogcimek.splice(cel, 0, elem);
  }

  mentJogcimek() {
    const jogcimek = this.jogcimek
      .filter((j) => String(j.nev || '').trim())
      .map((j) => ({ id: j.id, nev: String(j.nev).trim(), alap_osszeg: Number(j.alap_osszeg) || 0 }));
    this.http.put<any>(`/api/allomany-ber/jogcimek/${this.intezmenyId}`, { jogcimek }).subscribe({
      next: (r) => {
        this.jogcimek = (r?.jogcimek || []).map((j: any) => ({ ...j }));
        this.data.sendToast('A pótlék-gyűjtő mentve.');
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'A gyűjtő mentése nem sikerült.', 'error'),
    });
  }

  /* ── Bérváltozás rögzítése ── */

  nyit(d: any) {
    d.nyitva = !d.nyitva;
    if (!d.nyitva) return;
    if (!d.urlap) d.urlap = this.urlapEpit(d);
    if (!d.tortenet) this.tortenetBetolt(d);
  }

  /** A rögzítő űrlap a most látható bérből indul, a gyűjtő sorrendjében. */
  private urlapEpit(d: any): any {
    const meglevo = new Map<string, any>();
    for (const p of (d.potlekok || [])) meglevo.set(String(p.jogcim || '').trim(), p);

    const sorok = this.jogcimek.map((j) => {
      const nev = String(j.nev || '').trim();
      const p = meglevo.get(nev);
      meglevo.delete(nev);
      return {
        jogcim: nev,
        jogcim_id: j.id,
        osszeg: p ? Number(p.osszeg) || 0 : Number(j.alap_osszeg) || 0,
        be: !!p,
      };
    });

    return {
      ervenyes_tol: this.datum,
      alapber: Number(d.alapber) || 0,
      // Óraadónál ez a díjazás alapja; a havi végösszegbe nem számít bele.
      oradij: Number(d.oradij) || 0,
      //  Az óradíj két kiegészítése: a helyettesített és a nem szakos óra más díjazású
      //  lehet, mint a saját óra. Mindkettő szorzó, nem havi fizetés.
      helyettesites_oradij: Number(d.helyettesites_oradij) || 0,
      nemszakos_oradij: Number(d.nemszakos_oradij) || 0,
      megjegyzes: '',
      sorok,
      egyeb: [...meglevo.values()].map((p) => ({ jogcim: p.jogcim, osszeg: Number(p.osszeg) || 0 })),
    };
  }

  jogcimValt(urlap: any, sor: any, be: boolean) {
    sor.be = be;
    if (be && !Number(sor.osszeg)) {
      const j = this.jogcimek.find((x) => String(x.nev || '').trim() === sor.jogcim);
      sor.osszeg = Number(j?.alap_osszeg) || 0;
    }
  }

  ujEgyeb(urlap: any) {
    urlap.egyeb.push({ jogcim: '', osszeg: 0 });
  }

  torolEgyeb(urlap: any, i: number) {
    urlap.egyeb.splice(i, 1);
  }

  urlapOsszesen(urlap: any): number {
    const potlek = [...(urlap.sorok || []).filter((s: any) => s.be), ...(urlap.egyeb || [])]
      .reduce((s: number, p: any) => s + (Number(p.osszeg) || 0), 0);
    // Az óradíj NEM havi összeg, ezért nem adódik hozzá – a havi bér az óraszámtól függ.
    return (Number(urlap.alapber) || 0) + potlek;
  }

  rogzit(d: any) {
    const u = d.urlap;
    if (!u?.ervenyes_tol) { this.data.sendToast('Add meg, mikortól érvényes.', 'warning'); return; }

    const potlekok = [
      ...u.sorok.filter((s: any) => s.be)
        .map((s: any) => ({ jogcim: s.jogcim, jogcim_id: s.jogcim_id, osszeg: Number(s.osszeg) || 0 })),
      ...u.egyeb.filter((s: any) => String(s.jogcim || '').trim())
        .map((s: any) => ({ jogcim: String(s.jogcim).trim(), osszeg: Number(s.osszeg) || 0 })),
    ];

    this.http.put<any>(`/api/allomany-ber/valtozas/${encodeURIComponent(d.adid)}/${this.intezmenyId}`, {
      ervenyes_tol: this.nap(u.ervenyes_tol),
      alapber: Number(u.alapber) || 0,
      oradij: Number(u.oradij) || 0,
      helyettesites_oradij: Number(u.helyettesites_oradij) || 0,
      nemszakos_oradij: Number(u.nemszakos_oradij) || 0,
      megjegyzes: u.megjegyzes || '',
      potlekok,
    }).subscribe({
      next: () => {
        this.data.sendToast(`${d.nev} bére rögzítve ${this.nap(u.ervenyes_tol)}-tól. `
          + 'A korábbi időszak megmaradt.', 'success');
        d.tortenet = null;
        d.urlap = null;
        this.betolt();
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'A rögzítés nem sikerült.', 'error'),
    });
  }

  /* ── Történet ── */

  tortenetBetolt(d: any) {
    this.http.get<any[]>(`/api/allomany-ber/tortenet/${encodeURIComponent(d.adid)}/${this.intezmenyId}`).subscribe({
      next: (r) => { d.tortenet = this.idoszakok(Array.isArray(r) ? r : []); },
      error: () => { d.tortenet = []; },
    });
  }

  /** A tételek időszakonként csoportosítva (ahogy egy bérlap nézne ki). */
  private idoszakok(sorok: any[]): any[] {
    const terkep = new Map<string, any>();
    for (const s of sorok) {
      const kulcs = `${s.ervenyes_tol}|${s.ervenyes_ig || ''}`;
      if (!terkep.has(kulcs)) {
        terkep.set(kulcs, {
          tol: s.ervenyes_tol, ig: s.ervenyes_ig, megjegyzes: s.megjegyzes,
          alapber: 0, oradij: 0, helyettesites_oradij: 0, nemszakos_oradij: 0,
          potlekok: [], osszesen: 0,
        });
      }
      const i = terkep.get(kulcs);
      if (s.tipus === 'alapber') { i.alapber = s.osszeg; i.osszesen += s.osszeg; }
      else if (s.tipus === 'oradij') { i.oradij = s.osszeg; }   // nem havi összeg
      else if (s.tipus === 'helyettesites') { i.helyettesites_oradij = s.osszeg; }  // szorzó
      else if (s.tipus === 'nemszakos') { i.nemszakos_oradij = s.osszeg; }          // szorzó
      else { i.potlekok.push({ jogcim: s.jogcim, osszeg: s.osszeg }); i.osszesen += s.osszeg; }
    }
    return [...terkep.values()];
  }

  torolIdoszak(d: any, idoszak: any) {
    if (!confirm(`Törlöd a(z) ${idoszak.tol}-tól érvényes bérrögzítést? `
      + 'A megelőző időszak újra nyitottá válik.')) return;
    this.http.delete<any>(`/api/allomany-ber/idoszak/${encodeURIComponent(d.adid)}/${this.intezmenyId}`
      + `?tol=${idoszak.tol}`).subscribe({
      next: () => {
        this.data.sendToast('Időszak törölve.');
        d.tortenet = null;
        d.urlap = null;
        this.betolt();
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error'),
    });
  }
}
